import { useState, useEffect } from 'react'
import Lottie from 'lottie-react'

import RichText from '../../../components/RichText/RichText'

import { fetchClient } from '../../../utils/fetch'

export default function ImageTextBlocks (props = {
    heading: null,
    text: null,
    items: null
}) {
    const [items, setItems] = useState(null)

    const getItems = () => (items === null
            && Array.isArray(props.items)
        ? props.items
            .filter(obj => obj
                && (obj.lottie
                    || (obj.img || {}).src
                    || (obj.video || {}).src)
                && (obj.heading
                    || obj.text))
            .map((obj, idx) => Object.assign(obj, {
                lottieData: null,
                lottieRef: { current: null }
            }))
        : items) || []

    useEffect(async () => {
        const itemsData = [...getItems()]

        for (let i = 0; i < itemsData.length; i++) {
            if (!itemsData[i].lottie)
                continue

            itemsData[i].lottieData = await fetchClient
                .get(itemsData[i].lottie)
                .catch(err => console.warn(err))
        }

        setItems(itemsData
            .filter(obj => obj
                && ((obj.lottieData || {}).v
                    || (obj.img || {}).src
                    || (obj.video || {}).src)))
    }, [setItems])

    useEffect(() => {
        if (!(items || []).length)
            return

        items
            .filter(obj => obj.lottieData
                && obj.lottieRef.current)
            .forEach((obj) => {
                const lottieInterval = setInterval(() => {
                    const lottieRef = obj.lottieRef.current

                    const animationStateAttr = 'data-animation-state'

                    if (!lottieRef.animationLoaded
                            || !lottieRef.animationItem
                            || (lottieRef.animationItem
                                && lottieRef.animationItem.wrapper
                                && lottieRef.animationItem.wrapper
                                    .getAttribute(animationStateAttr)))
                        return

                    clearInterval(lottieInterval)

                    const { wrapper } = lottieRef.animationItem

                    const getAnimationStateAttr = () => wrapper
                        .getAttribute(animationStateAttr)

                    const setAnimationStateAttr = (state = null) => state
                        && getAnimationStateAttr() !== state
                        && wrapper.setAttribute(animationStateAttr, state)

                    setAnimationStateAttr('paused')

                    lottieRef.setSubframe(false)

                    const isVisible = () => {
                        const bounds = wrapper.getBoundingClientRect()

                        const isVisibleTop = bounds.top >= 0
                            && bounds.top < window.innerHeight

                        const isVisibleBottom = bounds.bottom >= 0
                            && bounds.bottom < window.innerHeight

                        return isVisibleTop
                            || isVisibleBottom
                    }

                    const isPlaying = () =>
                        getAnimationStateAttr() === 'playing'

                    const isPaused = () =>
                        getAnimationStateAttr() === 'paused'

                    const play = () => {
                        setAnimationStateAttr('playing')
                        lottieRef.play()
                    }

                    const pause = () => {
                        setAnimationStateAttr('paused')
                        lottieRef.pause()
                    }

                    if (isVisible())
                        play()

                    const setAnimationState = () => {
                        if (isPlaying()
                                && (!isVisible()
                                    || document.body
                                        .getAttribute('data-resizing')
                                    || document.visibilityState === 'hidden'))
                            return pause()

                        if (isVisible() && isPaused())
                            play()
                    }

                    let onScrollDebounced

                    window.addEventListener('scroll', () => {
                        setAnimationState()

                        if (onScrollDebounced)
                            clearTimeout(onScrollDebounced)

                        onScrollDebounced = setTimeout(setAnimationState, 300)
                    })

                    document
                        .addEventListener('visibilitychange', setAnimationState)
                }, 17)
            })
    }, [items])

    if (!getItems().length)
        return null

    return (
        <section className="image-text-blocks pad-section">
            <div className="container">
                {(props.heading || props.text)
                    && (
                        <div className="row flex-justify-content-center">
                            <div className="col-12 col-8-md col-6-lg-up">
                                <div className="text-align-center">
                                    {props.heading
                                        && (
                                            <h2 className="text-underscore-condensed-center">
                                                {props.heading}
                                            </h2>)}
                                    {props.text
                                        && (
                                            <p className="body-2">
                                                {props.text}
                                            </p>)}
                                </div>
                            </div>
                        </div>)}
                <ul
                    className={props.heading || props.text
                        ? 'pad-1-t-md-down'
                        : null}
                >
                    {getItems()
                        .map((obj, idx) => Object.assign(obj, {
                            even: !((idx + 1) % 2)
                        }))
                        .map((obj, idx) => (
                            <li
                                className="row flex-align-items-center flex-justify-content-center-sm pad-section pad-0-b"
                                key={`image-text-blocks-${idx}`}
                            >
                                <div
                                    className={`col-12 col-10-sm${
                                        obj.lottie || obj.video
                                            ? ` col-6-md col-7-lg-up pad-3-${
                                                obj.even
                                                    ? 'l'
                                                    : 'r'}-lg-up margin-2-b-sm-down margin-3-b-sm`
                                            : ' col-6-md-up'}${
                                        obj.img
                                                && obj.img.src
                                                && obj.imgRound
                                            ? ' col-5-xl-up col-offset-1-xl-up margin-3-b-md-down '
                                            : (!(obj.lottie || obj.video)
                                                ? ' col-offset-1-xl-up margin-2-b-sm-down margin-3-b-sm'
                                                : '')}${
                                        obj.even
                                            ? ' flex-order-last-md-up col-offset-1-xl-up'
                                            : ''}`}
                                >
                                    <div
                                        className={obj.lottie
                                            ? 'image-text-blocks-lottie'
                                            : (obj.img
                                                    && obj.img.src
                                                    && obj.imgRound
                                                ? 'ratio-square round'
                                                : null)}
                                        style={{
                                            height: obj.lottieWidth
                                                    && obj.lottieHeight
                                                ? '0px'
                                                : null,
                                            paddingBottom: obj.lottieWidth
                                                    && obj.lottieHeight
                                                ? `${(obj.lottieHeight
                                                    / obj.lottieWidth) * 100}%`
                                                : null
                                        }}
                                    >
                                        {!obj.lottie
                                            && obj.img
                                            && (
                                                <img
                                                    src={obj.img.src}
                                                    alt={obj.img.alt || ''}
                                                    width={obj.img.width}
                                                    height={obj.img.height}
                                                    data-object-fit={obj.imgRound
                                                        ? 'cover'
                                                        : null}
                                                />)}
                                        {obj.video
                                            && (
                                                <video
                                                    {...obj.video}
                                                    autoPlay
                                                    playsInline
                                                    loop
                                                    muted
                                                    data-object-fit="contain"
                                                    style={{
                                                        width: '100%'
                                                    }}
                                                />)}
                                        {!obj.video
                                            && obj.lottie
                                            && obj.lottieData
                                            && (
                                                <Lottie
                                                    lottieRef={obj.lottieRef}
                                                    animationData={obj.lottieData}
                                                    autoplay={false}
                                                    loop
                                                />)}
                                    </div>
                                </div>
                                <div
                                    className={`col-12 col-6-md col-5-lg col-4-xl-up ${
                                        obj.even
                                            ? ' flex-order-first-md-up'
                                            : ' col-offset-1-xl-up'}`}
                                >
                                    {obj.heading
                                        && <h3>{obj.heading}</h3>}
                                    {obj.text
                                        && (
                                            <p
                                                className={obj.heading
                                                    ? 'margin-3-t'
                                                    : null}
                                            >
                                                {obj.text}
                                            </p>)}
                                    {obj.cta?.href
                                        && obj.cta?.text
                                        && (
                                            <div
                                                className={obj.heading
                                                        || obj.text
                                                    ? 'margin-3-t'
                                                    : null}
                                            >
                                                <a
                                                    href={obj.cta.href}
                                                    className="btn btn-secondary pad-2"
                                                    style={{
                                                        fontSize: 18,
                                                        fontWeight: 400
                                                    }}
                                                >
                                                    <span>{obj.cta.text}</span>
                                                </a>
                                            </div>)}
                                    {obj.textSecondary
                                        && (
                                            <RichText
                                                className={`body-3${obj.heading
                                                        || obj.text
                                                        || (obj.cta?.href
                                                            && obj.cta?.text)
                                                    ? ' margin-3-t'
                                                    : ''}`}
                                                html={obj.textSecondary}
                                                style={{
                                                    color: 'var(--oxford)'
                                                }}
                                            />)}
                                </div>
                            </li>
                        ))}
                </ul>
            </div>
        </section>

    )
}
